<template>
  <div
    class="rounded-[16px] border border-gray-border bg-white shadow-panel-soft"
  >
    <div class="flex flex-col gap-3 p-3 lg:hidden">
      <template v-if="loading">
        <div
          v-for="index in 3"
          :key="`orders-reserved-mobile-loading-${index}`"
          class="rounded-[18px] bg-white-gray p-4"
        >
          <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
        </div>
      </template>

      <template v-else>
        <section
          v-for="group in tableOrders"
          :key="group.orderCode"
          class="overflow-hidden rounded-[18px] border border-gray-border bg-white"
        >
          <button
            type="button"
            class="flex w-full items-start justify-between gap-3 bg-white px-4 py-4 text-left"
            :aria-expanded="isGroupExpanded(group.orderCode)"
            @click="toggleGroup(group.orderCode)"
          >
            <div class="min-w-0">
              <p
                class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
              >
                Заказ
              </p>
              <p
                class="mt-1 text-[16px] leading-[1.25] font-helvetica-700 text-gray-dark"
              >
                №{{ group.orderCode }}
              </p>
              <p class="mt-1 text-[13px] text-extra-text-color">
                {{ group.orderDate || "Дата не указана" }}
              </p>
            </div>
            <div class="flex shrink-0 items-center gap-3">
              <div class="text-right">
                <p
                  class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
                >
                  Сумма
                </p>
                <p
                  class="mt-1 text-[16px] leading-[1.25] font-helvetica-700 text-gray-dark"
                >
                  {{ getGroupSum(group) }}
                </p>
              </div>
              <span
                class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-border bg-white text-extra-text-color transition-transform"
                :class="isGroupExpanded(group.orderCode) ? 'rotate-180' : ''"
              >
                <svg
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </div>
          </button>

          <div
            v-if="isGroupExpanded(group.orderCode)"
            class="border-t border-gray-border bg-white-gray/45 p-3"
          >
            <article
              v-for="item in group.items"
              :key="item.id"
              class="rounded-[16px] border border-gray-border bg-white-gray p-4"
            >
              <div class="flex gap-4">
                <div
                  class="flex h-[92px] w-[92px] shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-gray-border bg-white"
                >
                  <img
                    :src="item.image || '/logo.webp'"
                    alt="Товар"
                    class="max-h-full max-w-full object-contain"
                  />
                </div>
                <div class="min-w-0 flex-1">
                  <p
                    class="text-[16px] leading-[1.3] font-helvetica-700 text-gray-dark"
                  >
                    {{ item.title }}
                  </p>
                  <p class="mt-2 text-[14px] text-extra-text-color">
                    В резерве {{ item.reservedQty }} до {{ item.reserveUntil }}
                  </p>
                  <p class="text-[14px] text-extra-text-color">
                    Склад отгрузки: {{ item.warehouse }}
                  </p>
                </div>
              </div>

              <div class="mt-4 flex flex-col gap-2 text-[14px] text-gray-dark">
                <div class="flex items-start gap-2">
                  <img src="/tick.svg" class="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{{ item.statusMain }}</p>
                </div>
                <div class="flex items-start gap-2" v-if="item.statusSecondary">
                  <img src="/red_cros.svg" class="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{{ item.statusSecondary }}</p>
                </div>
              </div>

              <div
                class="mt-4 rounded-[14px] border border-gray-border bg-white px-4 py-3"
              >
                <dl
                  class="divide-y divide-gray-border text-[14px] leading-[20px] text-gray-dark"
                >
                  <div
                    class="grid grid-cols-[128px_minmax(0,1fr)] gap-3 pb-2.5"
                  >
                    <dt
                      class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
                    >
                      Количество
                    </dt>
                    <dd class="font-helvetica-700">{{ item.quantity }}</dd>
                  </div>
                  <div
                    class="grid grid-cols-[128px_minmax(0,1fr)] gap-3 py-2.5"
                  >
                    <dt
                      class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
                    >
                      Объем
                    </dt>
                    <dd>{{ item.volume }}</dd>
                  </div>
                  <div
                    class="grid grid-cols-[128px_minmax(0,1fr)] gap-3 py-2.5"
                  >
                    <dt
                      class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
                    >
                      Цена за шт.
                    </dt>
                    <dd class="font-helvetica-700">
                      <div class="flex flex-nowrap items-center gap-x-2">
                        <span class="shrink-0 whitespace-nowrap">{{
                          item.price
                        }}</span>
                        <DiscountPercentBadge
                          v-if="item.priceBadgeLabel || item.discountPercent"
                          :percent="item.discountPercent"
                          :label="item.priceBadgeLabel"
                          size="sm"
                        />
                      </div>
                      <span
                        v-if="item.discountPrice"
                        class="mt-1 block whitespace-nowrap text-[13px] text-extra-text-color line-through decoration-[var(--color-extra-text-color)]"
                      >
                        {{ item.discountPrice }}
                      </span>
                    </dd>
                  </div>
                  <div
                    class="grid grid-cols-[128px_minmax(0,1fr)] gap-3 pt-2.5"
                  >
                    <dt
                      class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
                    >
                      Сумма
                    </dt>
                    <dd class="font-helvetica-700">{{ item.sum }}</dd>
                  </div>
                </dl>
              </div>
            </article>
          </div>
        </section>

        <section
          v-if="tableOrders.length && totals"
          class="rounded-[18px] border border-gray-border bg-white px-4 py-4"
        >
          <dl
            class="divide-y divide-gray-border text-[14px] leading-[20px] text-gray-dark"
          >
            <div class="grid grid-cols-[112px_minmax(0,1fr)] gap-3 pb-2.5">
              <dt
                class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
              >
                Итого сумма
              </dt>
              <dd class="font-helvetica-700">{{ totals.sum }}</dd>
            </div>
            <div class="grid grid-cols-[112px_minmax(0,1fr)] gap-3 pt-2.5">
              <dt
                class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color"
              >
                Итого объем
              </dt>
              <dd>{{ totals.volume }}</dd>
            </div>
          </dl>
        </section>
      </template>
    </div>

    <div class="hidden overflow-x-auto lg:block">
      <table class="min-w-[940px] w-full table-fixed border-collapse text-left">
        <colgroup>
          <col class="w-[46%]" />
          <col class="w-[20%]" />
          <col class="w-[12%]" />
          <col class="w-[11%]" />
          <col class="w-[11%]" />
        </colgroup>
        <thead
          class="font-helvetica-700 font-bold text-[18px] text-gray-dark border-b border-gray-border"
        >
          <tr>
            <th class="py-4 pl-5 pr-3 font-bold">Товар</th>
            <th class="py-4 px-3 font-bold">Статус</th>
            <th class="py-4 px-3 font-bold">Количество</th>
            <th class="py-4 px-3 font-bold">Цена за шт.</th>
            <th class="py-4 px-3 font-bold">Сумма (₽)</th>
          </tr>
        </thead>
        <tbody
          class="divide-y divide-gray-border font-helvetica-400 font-normal text-[18px] text-gray-dark"
        >
          <tr v-if="loading">
            <td colspan="5" class="py-4 px-3">
              <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
            </td>
          </tr>
          <template v-else v-for="group in tableOrders" :key="group.orderCode">
            <tr class="bg-white-gray">
              <td
                colspan="5"
                class="py-4 pl-5 pr-3 font-helvetica-700 font-bold text-[18px] text-gray-dark"
              >
                Заказ №{{ group.orderCode }} от {{ group.orderDate }}
              </td>
            </tr>
            <tr v-for="item in group.items" :key="item.id" class="align-top">
              <td class="py-4 pl-5 pr-3">
                <div class="flex min-w-0 w-full gap-6">
                  <div
                    class="flex h-[160px] w-[160px] shrink-0 items-center justify-center"
                  >
                    <img
                      :src="item.image || '/logo.webp'"
                      alt="Товар"
                      class="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div
                    class="flex min-w-0 flex-1 flex-col justify-between gap-3"
                  >
                    <p
                      class="min-w-0 font-helvetica-700 text-[18px] leading-[28px] text-gray-dark"
                    >
                      {{ item.title }}
                    </p>
                    <div
                      class="font-helvetica-400 text-[16px] text-extra-text-color"
                    >
                      <p>
                        В резерве {{ item.reservedQty }} до
                        {{ item.reserveUntil }}
                      </p>
                      <p>Склад отгрузки: {{ item.warehouse }}</p>
                    </div>
                  </div>
                </div>
              </td>
              <td class="py-4 px-3 align-top">
                <div class="flex flex-col">
                  <div class="flex gap-[5px]">
                    <img src="/tick.svg" class="h-5 w-5 shrink-0" />
                    <p>{{ item.statusMain }}</p>
                  </div>
                  <div class="flex gap-[5px]" v-if="item.statusSecondary">
                    <img src="/red_cros.svg" class="h-5 w-5 shrink-0" />
                    <p>{{ item.statusSecondary }}</p>
                  </div>
                </div>
              </td>
              <td class="py-4 px-3 align-top whitespace-nowrap">
                <div class="flex flex-col">
                  <p>{{ item.quantity }}</p>
                  <p
                    class="font-helvetica-400 font-normal text-[16px] text-extra-text-color"
                  >
                    {{ item.volume }}
                  </p>
                </div>
              </td>
              <td class="py-4 px-3 align-top whitespace-nowrap">
                <div class="flex flex-col items-start gap-1">
                  <div class="flex flex-nowrap items-center gap-x-2">
                    <span class="shrink-0 whitespace-nowrap">{{
                      item.price
                    }}</span>
                    <DiscountPercentBadge
                      v-if="item.priceBadgeLabel || item.discountPercent"
                      :percent="item.discountPercent"
                      :label="item.priceBadgeLabel"
                      size="sm"
                    />
                  </div>
                  <span
                    v-if="item.discountPrice"
                    class="whitespace-nowrap text-[16px] text-extra-text-color line-through decoration-[var(--color-extra-text-color)]"
                  >
                    {{ item.discountPrice }}
                  </span>
                </div>
              </td>
              <td class="py-4 px-3 align-top whitespace-nowrap">
                {{ item.sum }}
              </td>
            </tr>
          </template>
        </tbody>
        <tfoot v-if="!loading && tableOrders.length && totals">
          <tr class="bg-white-gray">
            <td colspan="2"></td>
            <td
              class="py-4 pl-5 pr-3 font-helvetica-700 font-bold text-[18px] text-gray-dark"
            >
              <p>Итого:</p>
            </td>
            <td
              class="py-4 pl-5 pr-3 font-helvetica-700 font-bold text-[18px] text-gray-dark"
            >
              <p
                class="font-helvetica-400 font-normal text-[16px] text-extra-text-color"
              >
                {{ totals.volume }}
              </p>
            </td>
            <td
              class="py-4 pl-5 pr-3 font-helvetica-700 font-bold text-[18px] text-gray-dark"
            >
              <p>{{ totals.sum }}</p>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import DiscountPercentBadge from "~/components/DiscountPercentBadge.vue";

type ReservedItem = {
  id: string;
  image: string;
  title: string;
  reservedQty: number | string;
  reserveUntil: string;
  warehouse: string;
  statusMain: string;
  statusSecondary: string;
  quantity: string;
  volume: string;
  price: string;
  discountPrice?: string;
  discountPercent?: number | string;
  priceBadgeLabel?: string;
  sum: string;
};

type OrderGroup = {
  orderCode: string;
  orderDate: string;
  items: ReservedItem[];
};

type Totals = {
  volume: string;
  sum: string;
};

const props = defineProps<{
  orders?: OrderGroup[];
  totals?: Totals;
  loading?: boolean;
}>();

const tableOrders = computed(() => props.orders ?? []);
const totals = computed(() => props.totals ?? null);
const loading = computed(() => props.loading ?? false);
const expandedMap = ref<Record<string, boolean>>({});

const parseNumber = (value: string | number | null | undefined) =>
  Number(
    String(value ?? "")
      .replace(/[^\d.,-]/g, "")
      .replace(",", "."),
  );

const formatMoney = (value: number) =>
  value.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getGroupSum = (group: OrderGroup) => {
  const total = group.items.reduce(
    (acc, item) => acc + parseNumber(item.sum),
    0,
  );
  return formatMoney(total);
};

const isGroupExpanded = (orderCode: string) =>
  expandedMap.value[orderCode] ?? false;

const toggleGroup = (orderCode: string) => {
  expandedMap.value = {
    ...expandedMap.value,
    [orderCode]: !isGroupExpanded(orderCode),
  };
};
</script>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer-row {
  background: var(--gradient-shimmer);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
</style>
